import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { stashPendingPlan, readPendingPlan } from "@/lib/paddle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    CheckCircle2, ShieldCheck, Lock, ArrowRight, ArrowLeft, Building2,
    User as UserIcon, Mail as MailIcon, Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Signup — three-step onboarding wizard with two safety belts:
 *
 *   1. Probes `/api/signup/status` first. If public signup is paused
 *      (post-P0 lockdown), we show a proper "join the waitlist" state
 *      instead of a nasty 503 stack trace.
 *   2. The multi-step form makes it explicit that we are creating a
 *      *fresh, isolated workspace* — no shared data, no default tenant.
 *      Each workspace lives on its own tenant_id (fresh UUID, per the
 *      backend), so this wizard reinforces that in copy.
 */
const STEPS = [
    { key: "workspace", label: "Workspace" },
    { key: "account",   label: "Your account" },
    { key: "plan",      label: "Plan & consent" },
];

export default function Signup() {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [sp] = useSearchParams();

    const [status, setStatus] = useState({ enabled: true, message: "Create your workspace in under a minute.", waitlist_email: "pd@vaultbypd.com" });
    const [step, setStep] = useState(0);
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        workspace_name: "",
        name: "",
        email: "",
        password: "",
        mobile: "",
        plan: sp.get("plan") || "professional",
        consent_terms: false,
        consent_privacy: false,
        consent_marketing: false,
    });

    // Probe availability + preserve any incoming plan params.
    // NOTE: we intentionally do NOT gate the UI on `enabled=false`.
    // Public signup is unconditionally open (see backend `_signup_enabled`).
    // If the probe fails we simply leave the default open state and
    // let the POST /api/signup call surface any real error.
    useEffect(() => {
        api.get("/signup/status")
            .then((r) => setStatus((prev) => ({ ...prev, ...(r.data || {}), enabled: true })))
            .catch(() => { /* keep the default open state */ });
        api.get("/plans").then((r) => setPlans(r.data?.plans || [])).catch(() => setPlans([]));
    }, []);

    useEffect(() => {
        const plan = sp.get("plan"); const billing = sp.get("billing") || "monthly";
        if (plan) stashPendingPlan({ plan, billing, currency: sp.get("ccy"), ref: sp.get("ref"), promo: sp.get("promo") });
    }, [sp]);

    const canAdvance = () => {
        if (step === 0) return form.workspace_name.trim().length >= 2 && form.name.trim().length >= 2;
        if (step === 1) return /.+@.+\..+/.test(form.email) && form.password.length >= 8;
        if (step === 2) return form.consent_terms && form.consent_privacy;
        return false;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!canAdvance()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/signup", form);
            toast.success(`Welcome to VAULT — your workspace is ready.`);
            await refresh();
            try {
                const { isPlatformAuthenticatorAvailable, registerBiometric } =
                    await import("@/lib/webauthn");
                if (await isPlatformAuthenticatorAvailable()) {
                    if (window.confirm("Set up biometric unlock on this device? You can use Face ID, Touch ID or Windows Hello to unlock VAULT's Privacy Shield.")) {
                        await registerBiometric({
                            userId: data.user?.id || form.email,
                            userName: form.email,
                            deviceLabel: `Signup · ${navigator.platform || "device"}`,
                        });
                        toast.success("Biometric unlock enabled on this device.");
                    }
                }
            } catch { /* biometric optional */ }
            const pending = readPendingPlan();
            const dest = pending?.plan
                ? `/checkout/confirm?plan=${pending.plan}&billing=${pending.billing || "monthly"}`
                : "/setup";
            setTimeout(() => nav(dest, { replace: true }), 350);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const joinWaitlist = null; // Waitlist path removed — signup is unconditionally open.
    void joinWaitlist;

    const selectedPlan = plans.find((p) => p.id === form.plan);

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black text-white" data-testid="signup-page">
            <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8">
                {/* Left — brand + trust */}
                <div className="pt-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-8">
                        <span className="font-display text-4xl tracking-tight">VAULT</span>
                        <span className="text-amber-400/70 tracking-widest text-[10px] font-medium">BY PD</span>
                    </Link>
                    <h1 className="font-display text-4xl sm:text-5xl leading-tight">
                        Your Personal Finance Command Centre.
                    </h1>
                    <p className="text-slate-300 text-sm mt-4 max-w-md leading-relaxed">
                        Start free for 14 days. No credit card required. Bank-grade security,
                        role-based access, and monthly closing controls out of the box.
                    </p>

                    <ul className="mt-8 space-y-2.5 text-sm text-slate-300">
                        {[
                            "OCR-powered statement uploads",
                            "Automatic subscription discovery",
                            "Multi-user with roles + audit trail",
                            "CA-ready one-click Excel pack",
                            "Never stores CVV / OTP / full card numbers",
                        ].map((it) => (
                            <li key={it} className="flex items-start gap-2">
                                <CheckCircle2 size={13} className="text-emerald-400 mt-1 shrink-0" />
                                <span>{it}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 flex items-center gap-5 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> SOC-2 ready</span>
                        <span className="inline-flex items-center gap-1.5"><Lock size={12} /> AES-256 encrypted</span>
                        <span className="inline-flex items-center gap-1.5"><Building2 size={12} /> One tenant per workspace</span>
                    </div>
                </div>

                {/* Right — form or maintenance state */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-stone-900">
                    {status === null ? (
                        <div className="py-16 text-center text-stone-400 text-sm" data-testid="signup-loading">
                            Checking availability…
                        </div>
                    ) : (
                        // ─── Three-step wizard (unconditional) ────────────────
                        <form onSubmit={step === STEPS.length - 1 ? submit : (e) => { e.preventDefault(); if (canAdvance()) setStep(step + 1); }}>
                            {/* Step indicator */}
                            <div className="flex items-center gap-2 mb-6" data-testid="signup-steps">
                                {STEPS.map((s, i) => (
                                    <div key={s.key} className="flex items-center gap-2 flex-1">
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                                                i < step ? "bg-emerald-500 text-white" :
                                                i === step ? "bg-stone-900 text-white" :
                                                "bg-stone-100 text-stone-500"
                                            }`}
                                        >
                                            {i < step ? <CheckCircle2 size={12} /> : i + 1}
                                        </div>
                                        <div className={`text-[11px] font-medium truncate ${i === step ? "text-stone-900" : "text-stone-400"}`}>{s.label}</div>
                                        {i < STEPS.length - 1 && <div className="flex-1 h-px bg-stone-200" />}
                                    </div>
                                ))}
                            </div>

                            {step === 0 && (
                                <div className="space-y-4" data-testid="step-workspace">
                                    <div>
                                        <div className="eyebrow">Step 1 · Workspace</div>
                                        <h2 className="font-display text-3xl mt-2">Name your VAULT.</h2>
                                        <p className="text-xs text-stone-500 mt-2">
                                            Every VAULT is its own isolated workspace — your data lives on a
                                            fresh tenant, never mixed with anyone else&apos;s.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Workspace name *</Label>
                                        <div className="relative">
                                            <Building2 size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                            <Input
                                                value={form.workspace_name}
                                                onChange={(e) => setForm({ ...form, workspace_name: e.target.value })}
                                                placeholder="e.g. Sharma Household · Acme Ventures · Priya&apos;s Family"
                                                required
                                                className="pl-9"
                                                data-testid="signup-workspace"
                                            />
                                        </div>
                                        <div className="text-[11px] text-stone-500 mt-1.5">You can rename this later.</div>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Your name *</Label>
                                        <div className="relative">
                                            <UserIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                            <Input
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                placeholder="Your full name"
                                                required
                                                className="pl-9"
                                                data-testid="signup-name"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Mobile (optional)</Label>
                                        <Input
                                            value={form.mobile}
                                            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                            placeholder="For urgent alerts only"
                                            data-testid="signup-mobile"
                                        />
                                    </div>
                                </div>
                            )}

                            {step === 1 && (
                                <div className="space-y-4" data-testid="step-account">
                                    <div>
                                        <div className="eyebrow">Step 2 · Your account</div>
                                        <h2 className="font-display text-3xl mt-2">Set up sign-in.</h2>
                                        <p className="text-xs text-stone-500 mt-2">
                                            You&apos;ll be the owner-admin of <b>{form.workspace_name || "your workspace"}</b>.
                                            You can invite team members later.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Work email *</Label>
                                        <div className="relative">
                                            <MailIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                            <Input
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                required
                                                className="pl-9"
                                                data-testid="signup-email"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Password *</Label>
                                        <Input
                                            type="password"
                                            value={form.password}
                                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            required
                                            minLength={8}
                                            data-testid="signup-password"
                                        />
                                        <div className="text-[11px] text-stone-500 mt-1.5">
                                            At least 8 characters. We never store your CVV, OTP or full card number.
                                        </div>
                                    </div>
                                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-[11px] text-stone-600 flex gap-2 items-start">
                                        <Lock size={12} className="mt-0.5 text-stone-500 shrink-0" />
                                        <span>
                                            Passwords are bcrypt-hashed (cost 12). We can&apos;t read them —
                                            not even for support. Turn on biometric unlock right after signup for a smoother experience.
                                        </span>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-4" data-testid="step-plan">
                                    <div>
                                        <div className="eyebrow">Step 3 · Plan & consent</div>
                                        <h2 className="font-display text-3xl mt-2">Pick a plan.</h2>
                                        <p className="text-xs text-stone-500 mt-2">Every plan starts with a free trial. Change anytime.</p>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Plan</Label>
                                        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                                            <SelectTrigger data-testid="signup-plan"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {plans.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        {p.name}{p.trial_days > 0 ? ` · ${p.trial_days}-day trial` : ""}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {selectedPlan && (
                                            <div className="text-[11px] text-stone-500 mt-1.5 flex items-center gap-1">
                                                <Clock size={11} />
                                                {selectedPlan.tagline} · {selectedPlan.monthly_price_inr === 0
                                                    ? "Free forever"
                                                    : selectedPlan.monthly_price_inr == null
                                                        ? "Contact us"
                                                        : `₹${selectedPlan.monthly_price_inr}/mo`}
                                            </div>
                                        )}
                                    </div>
                                    <div className="pt-2 space-y-2 border-t border-stone-100">
                                        <label className="flex items-start gap-2 text-[12px] text-stone-700 cursor-pointer" data-testid="signup-consent-terms">
                                            <Checkbox checked={form.consent_terms} onCheckedChange={(v) => setForm({ ...form, consent_terms: !!v })} className="mt-0.5" />
                                            <span>I accept the <Link to="/terms" target="_blank" className="underline hover:text-stone-900">Terms of Use</Link></span>
                                        </label>
                                        <label className="flex items-start gap-2 text-[12px] text-stone-700 cursor-pointer" data-testid="signup-consent-privacy">
                                            <Checkbox checked={form.consent_privacy} onCheckedChange={(v) => setForm({ ...form, consent_privacy: !!v })} className="mt-0.5" />
                                            <span>I accept the <Link to="/privacy" target="_blank" className="underline hover:text-stone-900">Privacy Policy</Link></span>
                                        </label>
                                        <label className="flex items-start gap-2 text-[12px] text-stone-500 cursor-pointer">
                                            <Checkbox checked={form.consent_marketing} onCheckedChange={(v) => setForm({ ...form, consent_marketing: !!v })} className="mt-0.5" />
                                            <span>Send me occasional product updates (optional)</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Nav buttons */}
                            <div className="flex items-center gap-2 mt-6">
                                {step > 0 && (
                                    <Button
                                        type="button"
                                        onClick={() => setStep(step - 1)}
                                        variant="outline"
                                        className="text-stone-700"
                                        data-testid="signup-back"
                                    >
                                        <ArrowLeft size={13} className="mr-1.5" /> Back
                                    </Button>
                                )}
                                <div className="flex-1" />
                                <Button
                                    type="submit"
                                    disabled={busy || !canAdvance()}
                                    className="bg-stone-900 hover:bg-stone-800 text-white h-11 px-5 font-semibold"
                                    data-testid={step === STEPS.length - 1 ? "signup-submit" : "signup-next"}
                                >
                                    {step === STEPS.length - 1
                                        ? (busy ? "Creating your VAULT…" : "Create workspace")
                                        : "Continue"}
                                    <ArrowRight size={14} className="ml-2" />
                                </Button>
                            </div>

                            <div className="text-[12px] text-stone-500 text-center pt-4">
                                Already have an account? <Link to="/login" className="text-stone-900 font-medium underline">Sign in</Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
